import { useCallback, useEffect, useRef } from "react";
import { useSyncNoteTags } from "./useNotes";
import {
  enqueueSyncOp,
  getNote as dbGetNote,
  syncNoteLinksLocal,
  updateNote as updateNoteLocal,
} from "../lib/db";
import { extractBodyHashtags } from "../lib/hashtags";
import { AnalyticsEvent, track } from "../lib/analytics";
import {
  assembleMerge,
  CaptureCache,
  diff,
  extractNoteTitle,
  rebase,
  splitFrontmatterDocument,
  threeWayMerge,
} from "@caliu/utils";
import type { SyncStatus } from "@caliu/types";
import { composeFullContent } from "../lib/editorContent";
import { getSyncMode, readEngineRow } from "../lib/noteEngineState";
import { registerOpenEditor } from "../lib/openEditorRegistry";
import { kickLiveNote } from "../lib/services/liveNote";
import { scheduleSyncFlush } from "../lib/syncScheduler";
import i18next from "i18next";
import { toast } from "sonner";

const SAVE_DELAY_MS = 800;
/** While live, typing is captured about this often (PLAN.md "Editors (phase 2)"). */
const LIVE_CAPTURE_MS = 150;

type JsonNode = {
  type: string;
  text?: string;
  content?: JsonNode[];
  attrs?: Record<string, unknown>;
};


function extractTextFromNode(node: JsonNode): string {
  if (node.type === "text") return node.text ?? "";
  // Mention nodes (noteMention, hashtag, etc.) store their display text in attrs.label
  if (node.attrs?.label) return String(node.attrs.label);
  if (!node.content) return "";
  return node.content.map(extractTextFromNode).join("");
}

/** Derive the note title from Tiptap JSON or Markdown content (first node/line). */
export function extractTitleFromContent(content: string): string {
  return extractNoteTitle(content);
}

/** Returns true if there is body content beyond the first (title) node. */
export function hasBodyContent(content: string): boolean {
  if (!content) return false;
  if (content.trimStart().startsWith("{")) {
    try {
      const doc = JSON.parse(content) as JsonNode;
      if (doc.type === "doc" && doc.content) {
        return doc.content
          .slice(1)
          .some((n) => extractTextFromNode(n).trim() !== "");
      }
    } catch {
      /* fall through */
    }
  }
  // Markdown: more than one non-empty line
  return content.split("\n").filter((l) => l.trim()).length > 1;
}

export interface EditorSavePlan {
  /** What to store and push. */
  content: string;
  /** The buffer and the stored note changed the same lines: mark `conflict`. */
  conflict: boolean;
  /**
   * `content` is not the buffer: the stored note held text the editor does not
   * show. The editor is behind and should reload from the row when it can.
   */
  merged: boolean;
}

/**
 * The base is the raw stored row; the buffer is the editor's serialisation,
 * which drops a trailing newline (the native editors add one). Texts that
 * differ only by ONE trailing newline are the same text here, or the first
 * save after opening a note written elsewhere would read as "somebody else
 * changed it" and could become a false conflict.
 */
const dropTrailingNewline = (text: string) =>
  text.endsWith("\n") ? text.slice(0, -1) : text;

/**
 * Decide what an editor save writes.
 *
 * `base` is the stored content the editor's text derives from: what it loaded,
 * then what it last saved. While the stored note still holds exactly that,
 * nobody else wrote it and the buffer is saved as it is. When it does not,
 * something wrote the note under the editor, and saving the buffer as it is
 * would overwrite that write with text typed over the older content. So the
 * two are merged here, where both versions and their ancestor are at hand:
 *  - no overlap: the merge is saved (the buffer's edits on top of the stored
 *    note), and the stored row's `updated_at` is then the right
 *    `expected_updated_at`, because the saved text contains that row;
 *  - overlap, and the stored row is `synced`: the other text came from the
 *    server (a pull, or a push the server confirmed). The buffer is saved and
 *    the note goes to `conflict`, so the resolver offers both versions and the
 *    push holds the op meanwhile;
 *  - overlap, and the stored row is NOT `synced`: the other text was written
 *    on this device (a fold write, a bulk tag rewrite, an
 *    auto-merge). There is no server version to offer and nothing to resolve,
 *    so BOTH sides are kept: each overlapping region holds the buffer's lines
 *    and then the stored lines (`assembleMerge` "both"). Nothing typed and
 *    nothing written is lost. A frontmatter block is never doubled (two YAML
 *    blocks corrupt it): there the stored block wins.
 *
 * `base === null` means the editor never loaded this note (a save from a path
 * that has no buffer lineage): the buffer is saved as before.
 *
 * Pure. Mirrored in Swift and Kotlin (`NoteService.update`).
 */
export function planEditorSave(
  buffer: string,
  base: string | null,
  stored: string | undefined,
  storedStatus?: SyncStatus,
): EditorSavePlan {
  const asIs = { content: buffer, conflict: false, merged: false };
  if (base === null || stored === undefined) return asIs;
  const ancestor = dropTrailingNewline(base);
  const ours = dropTrailingNewline(buffer);
  const theirs = dropTrailingNewline(stored);
  if (theirs === ancestor || theirs === ours) return asIs;

  // The merge runs on the texts without that newline; the result gets the
  // BUFFER's trailing-newline state back.
  const tail = buffer.endsWith("\n") ? "\n" : "";
  const merge = threeWayMerge(ancestor, ours, theirs);
  if (merge.clean) {
    return merge.merged === ours
      ? asIs
      : { content: merge.merged + tail, conflict: false, merged: true };
  }
  if (storedStatus === "synced") {
    return { content: buffer, conflict: true, merged: false };
  }
  const keepBoth = assembleMerge(
    merge.hunks,
    merge.hunks
      .filter((hunk) => hunk.type === "conflict")
      .map((hunk) => (hunk.frontmatter ? "theirs" : "both")),
  );
  return { content: keepBoth + tail, conflict: false, merged: true };
}

/**
 * Whether the editor replaces its text with the stored note.
 *
 * Never while a save is pending: the editor holds keystrokes that are not
 * stored yet, and reloading would throw them away (the pending save merges
 * them with the stored note instead). Otherwise, when the stored text is not
 * what the editor holds and either a pull put it there (`synced`) or the last
 * save asked for it (`reloadRequested`: it wrote a merge the editor does not
 * show).
 */
export function shouldReloadEditor(input: {
  storedContent: string;
  storedStatus: SyncStatus | undefined;
  buffer: string;
  savePending: boolean;
  reloadRequested: boolean;
}): boolean {
  if (input.savePending || input.storedContent === input.buffer) return false;
  return input.storedStatus === "synced" || input.reloadRequested;
}

/**
 * v2 (PLAN.md "Client engine", caller rule 3): what an editor save stores.
 * The buffer `editorText` was typed over `base` (what the editor loaded or
 * last saved). When the row still holds `base`, the buffer is the text; when
 * something wrote the row since (a remote change, a fold write), the typing
 * is rebased over that change: `rebase(c0, editorText, current, diff(c0, current))`.
 * Never `localEdit(current, editorText)`: that would revert the other change.
 * Pure.
 */
export function planEngineSave(editorText: string, base: string | null, current: string): string {
  if (base === null || current === base || current === editorText) return editorText;
  return rebase(base, editorText, current, diff(base, current)).content;
}

/** Notes already told, this session, that their edits wait on a conflict. */
const conflictNoticeShown = new Set<string>();

/** What an editor remembers between saves. One per mounted editor. */
export interface EditorSaveState {
  /**
   * The stored content the editor's text derives from: set when the editor
   * loads or reloads the note, then after every save to the buffer that was
   * saved. See `planEditorSave`.
   */
  base: { noteId: string; content: string } | null;
  /** Note whose stored text is ahead of what the editor shows. */
  reloadNoteId: string | null;
  /**
   * While live: the editor's own serialisation at the sync point `base` is
   * from (`W`; `base` is the shared text then, `S`). A save captures with
   * `captureEdit(W, W', S)`, so the serialiser's normalisation never reaches
   * text nobody touched. Kept across a dropped socket; absent before live
   * started and in source mode: the buffer is the text.
   */
  editorText?: string;
  /** The capture's alignment, reused while the shared text moves only through this editor's saves. */
  capture?: CaptureCache;
  /**
   * The user's own edit (typing, or leaving source mode) emptied the note on
   * screen. A blank buffer is saved only then: `contentRef` is also "" when
   * the editor is waiting for a reload, and a flush of that must write
   * nothing. Spent by the save that stores the blank text.
   */
  userEmptied?: boolean;
}

/** Nothing but whitespace, or an empty first line and no body. */
const isBlankBuffer = (c: string) => !extractTitleFromContent(c) && !hasBodyContent(c);

/**
 * Persist the editor's buffer: one DB write, one sync op. Everything a save
 * decides is here and not in the hook, so it runs in a test without React.
 * `onEdited` fires when the buffer differs from what is stored; `syncTags`
 * receives the hashtags of the text that was stored.
 */
export function persistEditorBuffer(
  id: string,
  c: string,
  state: EditorSaveState,
  effects: {
    onEdited: () => void;
    syncTags: (tags: string[]) => void;
    /**
     * The editor's `serialise(parse(text))`, null when the editor is gone.
     * The capture's safety net (`captureEditChecked`): a capture that does
     * not read back like the editor's text falls back to the editor's text.
     */
    roundTrip?: () => ((text: string) => string) | null;
  },
): void {
  const title = extractTitleFromContent(c);
  const base = state.base?.noteId === id ? state.base.content : null;
  // A blank buffer is saved only when the user emptied the note on screen;
  // a note that never held anything, or a reload marker, writes nothing.
  if (isBlankBuffer(c)) {
    if (!state.userEmptied || base === null) return;
    state.userEmptied = false;
  }

  const existing = dbGetNote(id);

  // v2 and the note in the content engine: no conflicts, no diff3. The text
  // goes through the storage choke point (localEdit) and `/sync/content`, not
  // through an update op.
  if (existing && getSyncMode() === "v2" && readEngineRow(id)?.pending_ops != null) {
    const w = base !== null ? state.editorText : undefined;
    const shared =
      w !== undefined && base !== null
        ? (state.capture ??= new CaptureCache()).capture(w, c, base, effects.roundTrip?.() ?? undefined)
        : c;
    const saved = planEngineSave(shared, base, existing.content);
    state.base = { noteId: id, content: shared };
    if (w !== undefined) state.editorText = c;
    state.reloadNoteId = saved !== shared ? id : state.reloadNoteId === id ? null : state.reloadNoteId;
    if (saved === existing.content) return;
    if (shared !== existing.content) effects.onEdited();
    updateNoteLocal(id, {
      title: extractTitleFromContent(saved),
      content: saved,
      updatedAt: new Date().toISOString(),
      syncStatus: "pending",
    });
    syncNoteLinksLocal(id, saved);
    scheduleSyncFlush();
    kickLiveNote(id);
    effects.syncTags(extractBodyHashtags(saved));
    return;
  }

  const plan = planEditorSave(c, base, existing?.content, existing?.syncStatus);
  // After a merged save the stored note is ahead of the buffer, and stays
  // ahead until the editor reloads. Keeping the BUFFER as the base makes the
  // next save merge only what was typed since, instead of undoing the merge.
  state.base = { noteId: id, content: c };
  state.reloadNoteId = plan.merged ? id : state.reloadNoteId === id ? null : state.reloadNoteId;
  // The stored note already holds everything the buffer has (nothing typed
  // since the base): there is nothing to save, and writing would only replay
  // the stored text as an edit.
  if (
    existing &&
    plan.merged &&
    dropTrailingNewline(plan.content) === dropTrailingNewline(existing.content)
  ) {
    return;
  }

  if (c !== (existing?.content ?? "")) effects.onEdited();

  // A note that left `conflict` (resolved) is told again if it conflicts again.
  if (existing?.syncStatus !== "conflict" && !plan.conflict) {
    conflictNoticeShown.delete(id);
  }

  const expectedUpdatedAt = existing?.updatedAt;
  const now = new Date().toISOString();

  // Single DB write: content + title + syncStatus in one UPDATE
  const saved = plan.content;
  updateNoteLocal(id, {
    title: saved === c ? title : extractTitleFromContent(saved),
    content: saved,
    updatedAt: now,
    syncStatus: plan.conflict ? "conflict" : "pending",
  });
  syncNoteLinksLocal(id, saved);

  // The edit is kept on this device and its op is queued, but the push holds
  // it until the conflict is resolved. Say so once, so text that never
  // reaches the other devices is not a surprise.
  if (
    (plan.conflict || existing?.syncStatus === "conflict") &&
    !conflictNoticeShown.has(id)
  ) {
    conflictNoticeShown.add(id);
    toast(i18next.t("conflicts.description"), { id: `note-conflict-${id}` });
  }

  // Enqueue for sync engine directly (skip useUpdateNote to avoid a
  // second DB write + notifyChanges + useState re-render)
  enqueueSyncOp({
    type: "update",
    entity: "note",
    entityId: id,
    data: { content: saved, updated_at: now, expected_updated_at: expectedUpdatedAt },
    timestamp: Date.now(),
    retries: 0,
  });

  // Extract and sync hashtags — from the BODY only. `saved` is the full
  // document (frontmatter included); frontmatter YAML must never mint tags.
  effects.syncTags(extractBodyHashtags(saved));
}

/** The base takes a new frontmatter block and keeps its body (see `rebaseFrontmatter`). */
export function rebaseEditorFrontmatter(
  state: EditorSaveState,
  id: string,
  frontmatter: string | null,
): void {
  const base = state.base;
  if (base?.noteId !== id) return;
  base.content = composeFullContent(
    frontmatter,
    splitFrontmatterDocument(base.content).body,
  );
  if (state.editorText !== undefined) {
    state.editorText = composeFullContent(
      frontmatter,
      splitFrontmatterDocument(state.editorText).body,
    );
  }
}

interface UseEditorAutosaveOptions {
  noteId: string;
  /**
   * The note's socket is live and the rich editor shows it: typing is
   * captured every ~150 ms, and remote text goes to `onRemoteText` (applied
   * in place) instead of a reload.
   */
  live?: boolean;
  onRemoteText?: () => void;
  /** The editor's round trip for the capture's safety net (see `persistEditorBuffer`). */
  roundTrip?: () => ((text: string) => string) | null;
}

export function useEditorAutosave({ noteId, live = false, onRemoteText, roundTrip }: UseEditorAutosaveOptions) {
  const roundTripRef = useRef(roundTrip);
  roundTripRef.current = roundTrip;
  const liveRef = useRef(live);
  liveRef.current = live;
  const onRemoteTextRef = useRef(onRemoteText);
  onRemoteTextRef.current = onRemoteText;
  const syncNoteTags = useSyncNoteTags();

  // contentRef holds the FULL note document (frontmatter + body markdown,
  // canonical format) — never a body-only string. NoteEditor's onUpdate
  // composes frontmatter + editor body and writes it back here, so the
  // debounced timer below and every flushSave(contentRef.current) call site
  // persist the whole document (frontmatter included). Legacy
  // notes stored as Tiptap JSON are converted to markdown on first edit.
  const contentRef = useRef("");

  const saveStateRef = useRef<EditorSaveState>({ base: null, reloadNoteId: null });
  /** The editor now shows exactly this stored content. */
  const markLoaded = useCallback((id: string, content: string, editorText?: string) => {
    saveStateRef.current = {
      base: { noteId: id, content },
      reloadNoteId: null,
      ...(editorText !== undefined ? { editorText } : {}),
    };
  }, []);
  /**
   * Live starts (or stops) for the note on screen. Starting takes the
   * editor's serialisation now as `W` at the current sync point; stopping
   * drops it, and the buffer is the text again.
   */
  const setEditorText = useCallback((id: string, editorText: string | undefined) => {
    const state = saveStateRef.current;
    if (state.base?.noteId !== id) return;
    if (editorText === undefined) delete state.editorText;
    else state.editorText = editorText;
  }, []);
  /**
   * A writer changed the note's frontmatter (a fold write) and the editor
   * adopted that block, but its BODY is still what it was. So the base keeps
   * its body and takes the new block: marking the whole stored note as loaded
   * would claim the editor shows a body it does not (text pulled or merged
   * under it), and the next save would then overwrite that text.
   */
  const rebaseFrontmatter = useCallback((id: string, frontmatter: string | null) => {
    rebaseEditorFrontmatter(saveStateRef.current, id, frontmatter);
  }, []);
  /** The user's own edit left the note on screen as `content` (see `userEmptied`). */
  const markUserEdit = useCallback((id: string, content: string) => {
    const state = saveStateRef.current;
    if (state.base?.noteId === id) state.userEmptied = isBlankBuffer(content);
  }, []);
  /** Whether saves of this note capture against the editor's serialisation (`W` is set). */
  const capturing = useCallback(
    (id: string) => saveStateRef.current.base?.noteId === id && saveStateRef.current.editorText !== undefined,
    [],
  );
  /** The shared text the editor shows (its last sync point), or null. */
  const syncPointText = useCallback((id: string): string | null => {
    const base = saveStateRef.current.base;
    return base?.noteId === id ? base.content : null;
  }, []);
  /** The last save stored text the editor does not show (see shouldReloadEditor). */
  const reloadRequested = useCallback(
    (id: string) => saveStateRef.current.reloadNoteId === id,
    [],
  );

  const syncNoteTagsRef = useRef(syncNoteTags);
  syncNoteTagsRef.current = syncNoteTags;

  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Note whose `note_edited` has already been reported this session. */
  const lastEditedTrackedNoteId = useRef<string | null>(null);

  /** Persist to SQLite + enqueue for sync. Writes to the DB exactly once
   *  (not twice via useUpdateNote) and enqueues the sync op directly.
   *  All the expensive work lives here so it NEVER runs inside an input handler. */
  const persistSave = useCallback((id: string, c: string) => {
    persistEditorBuffer(id, c, saveStateRef.current, {
      // One `note_edited` per editing session, not per persist — mirrors
      // AutosaveService.trackNoteEditedOnce on Swift. Gated on the content
      // actually differing from what is on disk, because `flushSave` also
      // lands here from paths that persist without the user typing: entering
      // and leaving source mode, Cmd+S, and the history-restore flush hook.
      onEdited: () => {
        if (lastEditedTrackedNoteId.current === id) return;
        lastEditedTrackedNoteId.current = id;
        track(AnalyticsEvent.NoteEdited);
      },
      syncTags: (tags) => void syncNoteTagsRef.current.mutate({ noteId: id, tags }),
      roundTrip: () => roundTripRef.current?.() ?? null,
    });
  }, []);

  // Flush any pending debounced save immediately
  const flushSave = useCallback((id: string, c: string) => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    persistSave(id, c);
  }, [persistSave]);

  // Called on every editor update. Only updates the in-memory ref and
  // resets the debounce timer. No DB writes, no React re-renders — the
  // input handler stays fast.
  const scheduleSave = useCallback(
    (newContent: string) => {
      markUserEdit(noteId, newContent);
      // Live: a capture already scheduled takes this keystroke too (it reads
      // contentRef when it fires), so typing is captured about every 150 ms
      // rather than once it pauses.
      if (liveRef.current && saveTimerRef.current) return;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        saveTimerRef.current = null;
        persistSave(noteId, contentRef.current);
      }, liveRef.current ? LIVE_CAPTURE_MS : SAVE_DELAY_MS);
    },
    [noteId, persistSave, markUserEdit],
  );

  // When noteId changes: flush any pending save for the OLD note and reset tracking
  // refs. We intentionally do NOT clear the editor here — keeping the previous note's
  // content visible avoids a white flash while the new note loads from IndexedDB.
  // The populate effect below will replace the content once the new note arrives.
  useEffect(() => {
    const capturedNoteId = noteId;

    return () => {
      // Flush pending save for the note we're leaving
      if (saveTimerRef.current) {
        flushSave(capturedNoteId, contentRef.current);
      }
      // Reset tracking ref for the incoming note
      contentRef.current = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteId]);

  // Caller rule 2: the content sync driver flushes this editor's typing
  // before it applies remote ops to the note, and asks for a reload after.
  useEffect(
    () =>
      registerOpenEditor(noteId, {
        flush: () => {
          if (saveTimerRef.current) flushSave(noteId, contentRef.current);
        },
        requestReload: () => {
          // Live: the editor takes the remote text in place (applyRemote).
          const apply = liveRef.current ? onRemoteTextRef.current : undefined;
          if (apply) apply();
          else saveStateRef.current.reloadNoteId = noteId;
        },
      }),
    [noteId, flushSave],
  );

  // Clear timer on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  return {
    flushSave,
    scheduleSave,
    markUserEdit,
    contentRef,
    saveTimerRef,
    markLoaded,
    setEditorText,
    syncPointText,
    rebaseFrontmatter,
    reloadRequested,
    capturing,
  };
}
